// client/src/pages/Admin/CourseList/index.jsx
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
// import customFetch from '../../../api/customFetch';
import customFetch from "../../api/customFetch.js";

export default function AdminCourseList() {
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all'); // all, published, draft
  const [searchTerm, setSearchTerm] = useState('');
  const nav = useNavigate();

  useEffect(() => {
    loadCourses();
  }, []);

  const loadCourses = async () => {
    try {
      setLoading(true);
      setError(null);

      const { data } = await customFetch.get('/courses/admin');
      
      if (data.success && Array.isArray(data.courses)) {
        setCourses(data.courses);
      } else if (Array.isArray(data)) {
        setCourses(data);
      } else {
        setError('פורמט תשובה לא תקין מהשרת');
        setCourses([]);
      }
    } catch (e) {
      console.error('Error fetching courses:', e);
      if (e.response?.status === 401 || e.response?.status === 403) {
        nav('/admin/login');
      } else {
        setError('שגיאה בטעינת רשימת הקורסים');
      }
    } finally {
      setLoading(false);
    }
  };

  const togglePublish = async (course, e) => {
    e.stopPropagation();
    try {
      const endpoint = course.isPublished 
        ? `/courses/admin/${course.slug}/unpublish`
        : `/courses/admin/${course.slug}/publish`;
      
      await customFetch.patch(endpoint);
      
      setCourses(courses.map(c => 
        c.slug === course.slug 
          ? { ...c, isPublished: !c.isPublished }
          : c
      ));
    } catch (error) {
      console.error('Error toggling publish:', error);
      setError('שגיאה בשינוי סטטוס הפרסום');
    }
  };

  // סינון וחיפוש
  const filteredCourses = courses
    .filter(course => {
      if (filter === 'published') return course.isPublished;
      if (filter === 'draft') return !course.isPublished;
      return true;
    })
    .filter(course => {
      if (!searchTerm.trim()) return true;
      const search = searchTerm.toLowerCase();
      return (
        course.title?.toLowerCase().includes(search) ||
        course.slug?.toLowerCase().includes(search)
      );
    });

  const getStatusCounts = () => ({
    all: courses.length,
    published: courses.filter(c => c.isPublished).length,
    draft: courses.filter(c => !c.isPublished).length,
  });

  const counts = getStatusCounts();

  if (loading) {
    return (
      <div className="admin-page">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <span>טוען לומדות...</span>
        </div>
      </div>
    );
  }

  return (
    <div className='admin-page'>
      {/* Page Header */}
      <div className='page-header'>
        <h1 className='page-title'>
          <span className='page-title-icon'>📚</span>
          ניהול לומדות
          <span className='badge info' style={{ marginRight: '8px' }}>
            {filteredCourses.length}
          </span>
        </h1>
        <div className='page-actions'>
          <button
            className='admin-btn primary'
            onClick={() => nav('/admin/courses/new')}
          >
            <span>➕</span>
            <span>לומדה חדשה</span>
          </button>
        </div>
      </div>

      {/* Error Alert */}
      {error && (
        <div className='admin-alert error'>
          <span>⚠️</span>
          <span>{error}</span>
          <button className='alert-close' onClick={() => setError(null)}>
            ✕
          </button>
        </div>
      )}

      {/* Filters Panel */}
      <div className='filters-panel'>
        <div className='filters-grid'>
          <div className='form-group' style={{ marginBottom: 0 }}>
            <label className='admin-label'>סינון לפי סטטוס</label>
            <select
              className='admin-select'
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
            >
              <option value='all'>כל הלומדות ({counts.all})</option>
              <option value='published'>מפורסמות ({counts.published})</option>
              <option value='draft'>טיוטות ({counts.draft})</option>
            </select>
          </div>

          <div className='form-group' style={{ marginBottom: 0 }}>
            <label className='admin-label'>חיפוש</label>
            <input
              type='text'
              className='admin-input'
              placeholder='חפש לפי שם או מזהה...'
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          <div
            className='form-group'
            style={{ marginBottom: 0, alignSelf: 'end' }}
          >
            <button className='admin-btn secondary' onClick={loadCourses}>
              🔄 רענן
            </button>
          </div>
        </div>
      </div>

      {/* Summary Stats */}
      {courses.length > 0 && (
        <div className='admin-card' style={{ marginTop: '24px', marginBottom: '24px' }}>
          <div className='card-header'>
            <h3 className='card-title'>📊 סיכום</h3>
          </div>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
              gap: '20px',
              textAlign: 'center',
            }}
          >
            <div>
              <div
                style={{
                  fontSize: '2rem',
                  fontWeight: '700',
                  color: 'var(--admin-primary)',
                }}
              >
                {courses.length}
              </div>
              <div style={{ color: 'var(--admin-text-muted)' }}>
                סה"כ לומדות
              </div>
            </div>
            <div>
              <div
                style={{
                  fontSize: '2rem',
                  fontWeight: '700',
                  color: 'var(--admin-success)',
                }}
              >
                {counts.published}
              </div>
              <div style={{ color: 'var(--admin-text-muted)' }}>מפורסמות</div>
            </div>
            <div>
              <div
                style={{
                  fontSize: '2rem',
                  fontWeight: '700',
                  color: 'var(--admin-warning)',
                }}
              >
                {counts.draft}
              </div>
              <div style={{ color: 'var(--admin-text-muted)' }}>טיוטות</div>
            </div>
            <div>
              <div
                style={{
                  fontSize: '2rem',
                  fontWeight: '700',
                  color: 'var(--admin-accent)',
                }}
              >
                {courses.reduce(
                  (sum, c) => sum + (c.questions?.length || 0),
                  0
                )}
              </div>
              <div style={{ color: 'var(--admin-text-muted)' }}>סה"כ שאלות</div>
            </div>
          </div>
        </div>
      )}

      {/* Courses List */}
      {filteredCourses.length === 0 ? (
        <div className='empty-state'>
          <div className='empty-state-icon'>📚</div>
          <h3>
            {courses.length === 0
              ? 'אין לומדות במערכת'
              : 'לא נמצאו לומדות התואמות לחיפוש'}
          </h3>
          <p>
            {courses.length === 0
              ? 'צור את הלומדה הראשונה שלך'
              : 'נסה לשנות את הסינונים'}
          </p>
          {courses.length === 0 && (
            <button
              className='admin-btn primary'
              onClick={() => nav('/admin/courses/new')}
            >
              ➕ צור לומדה ראשונה
            </button>
          )}
        </div>
      ) : (
        <div className='data-grid'>
          {filteredCourses.map((course) => (
            <div
              key={course._id}
              className='data-item'
              onClick={() => nav(`/admin/courses/${course.slug}`)}
            >
              <div className='data-item-content'>
                <h3>
                  {course.title || 'ללא כותרת'}
                  <span
                    className={`badge ${
                      course.isPublished ? 'success' : 'warning'
                    }`}
                  >
                    {course.isPublished ? '🟢 מפורסם' : '⚪ טיוטה'}
                  </span>
                </h3>

                <div className='data-item-meta'>
                  <span>
                    <strong>מזהה:</strong> {course.slug}
                  </span>
                  <span>
                    <strong>שקפים:</strong> {course.slides?.length || 0}
                  </span>
                  <span>
                    <strong>שאלות:</strong> {course.questions?.length || 0}
                  </span>
                  <span>
                    <strong>ציון עובר:</strong> {course.passScore || 80}%
                  </span>
                </div>

                {course.targetSectors && course.targetSectors.length > 0 && (
                  <div style={{ marginTop: '8px' }}>
                    <strong
                      style={{
                        fontSize: '0.8rem',
                        color: 'var(--admin-text-muted)',
                      }}
                    >
                      סקטורים:{' '}
                    </strong>
                    <span
                      style={{
                        fontSize: '0.8rem',
                        color: 'var(--admin-text-muted)',
                      }}
                    >
                      {course.targetSectors.slice(0, 3).join(', ')}
                      {course.targetSectors.length > 3 &&
                        ` +${course.targetSectors.length - 3}`}
                    </span>
                  </div>
                )}

                {course.createdAt && (
                  <div
                    style={{
                      fontSize: '0.75rem',
                      color: 'var(--admin-text-muted)',
                      marginTop: '8px',
                    }}
                  >
                    נוצר:{' '}
                    {new Date(course.createdAt).toLocaleDateString('he-IL')}
                    {course.updatedAt && (
                      <>
                        {' '}
                        | עודכן:{' '}
                        {new Date(course.updatedAt).toLocaleDateString('he-IL')}
                      </>
                    )}
                  </div>
                )}
              </div>

              <div className='data-item-actions'>
                <button
                  className='admin-btn primary sm'
                  onClick={(e) => {
                    e.stopPropagation();
                    nav(`/admin/courses/${course.slug}`);
                  }}
                >
                  ✏️ ערוך
                </button>

                <button
                  className={`admin-btn sm ${
                    course.isPublished ? 'warning' : 'success'
                  }`}
                  onClick={(e) => togglePublish(course, e)}
                >
                  {course.isPublished ? '📤 הסר מפרסום' : '📥 פרסם'}
                </button>

                <button
                  className='admin-btn secondary sm'
                  onClick={(e) => {
                    e.stopPropagation();
                    window.open(`/${course.slug}`, '_blank');
                  }}
                >
                  👁️ צפה
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}